import React, { useState } from 'react'
import { Share2, Check, Copy, ExternalLink } from 'lucide-react'
import usePlayStore from '../../stores/playStore'

const PlayShare = () => {
  const { currentPlay, originalLoadedPlay } = usePlayStore()
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('share') // 'share', 'error', 'warning', 'success', 'noGif'
  const [shareLink, setShareLink] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)

  const handleShare = () => {
    // Check if play is saved
    if (!originalLoadedPlay || !originalLoadedPlay.id) {
      setModalType('error')
      setShowModal(true)
      return
    }

    // Check if play has a GIF
    if (!currentPlay.gifUrl) {
      setModalType('noGif')
      setShowModal(true)
      return
    }

    // Generate share link
    const link = `${window.location.origin}/play-view/${originalLoadedPlay.id}`
    setShareLink(link)

    // Check if play is public
    if (!currentPlay.isPublic) {
      setModalType('warning')
      setShowModal(true)
    } else {
      // If public, directly copy to clipboard
      copyToClipboard(link)
    }
  }

  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link)
      setLinkCopied(true)
      setModalType('success')
      setShowModal(true)
      
      // Reset copied state after 3 seconds
      setTimeout(() => {
        setLinkCopied(false)
      }, 3000)
    } catch (error) {
      console.error('Failed to copy link:', error)
      // Fallback: show link in modal for manual copy
      setModalType('share')
      setShowModal(true)
    }
  }

  const handleCopyLink = async () => {
    await copyToClipboard(shareLink)
  }

  const handleOpenInNewTab = () => {
    window.open(shareLink, '_blank', 'noopener,noreferrer')
  }

  const handleWarningOk = () => {
    // User acknowledged warning, now copy the link
    copyToClipboard(shareLink)
  }

  const closeModal = () => {
    setShowModal(false)
    setLinkCopied(false)
  }

  return (
    <>
      <button
        onClick={handleShare}
        className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
      >
        <Share2 size={14} />
        <span>Share</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            {modalType === 'error' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-red-600">Cannot Share</h3>
                <p className="text-gray-700 mb-4">
                  Please save the play before sharing.
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    OK
                  </button>
                </div>
              </>
            )}

            {modalType === 'noGif' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-yellow-600">No GIF Available</h3>
                <p className="text-gray-700 mb-4">
                  This play does not have a GIF yet. Please generate a GIF first before sharing.
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    OK
                  </button>
                </div>
              </>
            )}

            {modalType === 'warning' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-yellow-600">Limited Access</h3>
                <p className="text-gray-700 mb-4">
                  This play is not public. Access will be limited to the current owner.
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWarningOk}
                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
                  >
                    OK
                  </button>
                </div>
              </>
            )}

            {modalType === 'success' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-green-600 flex items-center space-x-2">
                  <Check size={20} />
                  <span>Link Copied!</span>
                </h3>
                <p className="text-gray-700 mb-4">
                  The share link has been copied to your clipboard.
                </p>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 mb-4">
                  <p className="text-xs text-gray-500 mb-1">Share Link:</p>
                  <p className="text-sm text-gray-800 break-all">{shareLink}</p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleOpenInNewTab}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center space-x-2"
                  >
                    <ExternalLink size={16} />
                    <span>Open in New Tab</span>
                  </button>
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </>
            )}

            {modalType === 'share' && (
              <>
                <h3 className="text-lg font-semibold mb-4">Share Play</h3>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 mb-4">
                  <p className="text-xs text-gray-500 mb-1">Share Link:</p>
                  <p className="text-sm text-gray-800 break-all">{shareLink}</p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleOpenInNewTab}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center space-x-2"
                  >
                    <ExternalLink size={16} />
                    <span>Open in New Tab</span>
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center space-x-2"
                  >
                    {linkCopied ? (
                      <>
                        <Check size={16} />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        <span>Copy Link</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default PlayShare

